
import React from 'react';
import { StudentCounselModel } from '../../../../models';
import { Calendar, MessageSquare, Star, User, Reply, ArrowLeft } from 'lucide-react';

interface StudentCounselDetailViewProps {
    log: StudentCounselModel;
    onBack: () => void;
}

const StudentCounselDetailView: React.FC<StudentCounselDetailViewProps> = ({ log, onBack }) => {
    const isStudent = (log as any).isStudentWrite;

    React.useEffect(() => {
        if (log.reply && log.isStudentRead === false) {
            const markAsRead = async () => {
                try {
                    const { doc, updateDoc } = await import('firebase/firestore');
                    const { db } = await import('../../../../firebase');
                    await updateDoc(doc(db, 'studentCounsel', log.id), {
                        isStudentRead: true
                    });
                } catch (error) {
                    console.error('Error marking counsel as read:', error);
                }
            };
            markAsRead();
        }
    }, [log.id, log.reply, log.isStudentRead]);

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex items-center gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <button
                    onClick={onBack}
                    className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all flex items-center justify-center"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className={`w-7 h-7 rounded-lg ${isStudent ? 'bg-indigo-50 text-indigo-600' : 'bg-rose-50 text-rose-600'} flex items-center justify-center`}>
                            <MessageSquare size={14} />
                        </div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight">{log.title}</h2>
                    </div>
                    <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                        <Calendar size={12} />
                        {log.date}
                    </p>
                </div>
            </div>

            {/* Content */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-8">
                {/* Meta Info */}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className={`${isStudent ? 'bg-indigo-600' : 'bg-rose-500'} text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg shadow-sm`}>
                        {log.tag}
                    </span>
                    {isStudent && (
                        <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border border-indigo-100">내가 보낸 메시지</span>
                    )}
                </div>

                {/* Counselor Info */}
                <div className="flex items-center gap-4 bg-slate-50 px-5 py-3 rounded-xl border border-slate-100 w-fit">
                    <div className={`w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center ${isStudent ? 'text-indigo-600' : 'text-rose-500'} text-xs font-black shadow-sm`}>
                        <User size={18} />
                    </div>
                    <div className="text-left">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter leading-none mb-1">{isStudent ? 'Student' : 'Counselor'}</p>
                        <p className="text-xs font-black text-slate-800">{log.counselor}</p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="p-8 bg-slate-50/50 rounded-2xl border border-slate-100">
                    <p className="text-sm font-bold text-slate-600 leading-relaxed whitespace-pre-wrap">
                        {log.content}
                    </p>
                </div>

                {/* Reply Section */}
                {log.reply && (
                    <div className="p-8 bg-emerald-50 rounded-2xl border-2 border-emerald-200 space-y-4 animate-in slide-in-from-top duration-500">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-100">
                                    <Reply size={16} />
                                </div>
                                <span className="text-xs font-black text-emerald-700 uppercase tracking-widest">선생님 답변</span>
                            </div>
                            {log.repliedAt && (
                                <span className="text-[10px] font-bold text-emerald-500 flex items-center gap-1">
                                    <Calendar size={12} /> {new Date(log.repliedAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                        <div className="bg-white/60 p-6 rounded-2xl border border-emerald-100">
                            <p className="text-sm font-bold text-slate-700 leading-relaxed whitespace-pre-wrap">
                                {log.reply}
                            </p>
                        </div>
                        {log.repliedBy && (
                            <div className="flex items-center gap-2 pt-2 border-t border-emerald-100">
                                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-[10px] font-black">
                                    {log.repliedBy[0]}
                                </div>
                                <span className="text-[10px] font-bold text-emerald-600">답변자: {log.repliedBy}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Status Footer */}
                <div className="flex items-center justify-between border-t border-slate-100 pt-6">
                    <div className="flex items-center gap-1.5">
                        {isStudent ? (
                            log.reply ? (
                                <div className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5">
                                    <Reply size={12} /> 답변 완료
                                </div>
                            ) : (
                                <div className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">Message Sent</div>
                            )
                        ) : (
                            <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <Star key={i} size={14} className="fill-rose-400 text-rose-400" />
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="text-[11px] font-black text-slate-300 uppercase tracking-[0.2em]">
                        {isStudent ? (log.reply ? 'Response Received' : 'Waiting for Response') : 'Verified Feedback'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentCounselDetailView;
